export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  imageSeq: string;
  imageWidth: number;
  imageHeight: number;
  tags: string[];
}

export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
}

export interface FAQ {
  question: string;
  answer: string;
}

export const categories: Category[] = [
  {
    slug: 'platform-intro',
    name: '平台介绍',
    description: '了解 17c.com 平台的基本信息、发展历程和核心定位',
    icon: 'ri-global-line',
  },
  {
    slug: 'user-guide',
    name: '使用指南',
    description: '详细的注册登录、功能操作及平台使用教程',
    icon: 'ri-book-open-line',
  },
  {
    slug: 'features',
    name: '功能解析',
    description: '深入解读 17c.com 各项核心功能的使用场景与优势',
    icon: 'ri-apps-2-line',
  },
  {
    slug: 'resources',
    name: '资源中心',
    description: '汇集 17c.com 平台提供的各类资源、工具和下载内容',
    icon: 'ri-folder-download-line',
  },
  {
    slug: 'faq',
    name: '常见问题',
    description: '关于 17c.com 平台使用过程中遇到的常见问题与解答',
    icon: 'ri-question-answer-line',
  },
];

export const articles: Article[] = [
  {
    slug: 'getting-started-guide',
    title: '17c.com 注册登录完整教程：从零开始快速上手',
    excerpt: '本教程将手把手教您如何在 17c.com 完成账号注册、邮箱验证、资料设置以及安全登录的全部流程，帮助您在三分钟内顺利进入平台开始使用。',
    content: `<h2>第一步：访问 17c.com 官方入口</h2>
<p>打开浏览器，在地址栏输入 <strong>www.17c.com</strong> 即可进入 17c.com 平台首页。建议使用 Chrome、Firefox、Edge 等主流浏览器以获得最佳体验。首页提供了清晰的导航结构，您可以在页面右上角找到「注册」和「登录」按钮。</p>

<h2>第二步：注册您的账号</h2>
<p>点击「注册」按钮后，系统会引导您进入注册页面。您需要填写以下信息：</p>
<ul>
<li><strong>电子邮箱：</strong>请使用您常用的有效邮箱地址，这将用于账号验证和重要通知。</li>
<li><strong>用户名：</strong>设置一个易于记忆且未被占用的用户名，支持中英文及数字组合。</li>
<li><strong>密码：</strong>设置 8-20 位的安全密码，建议包含大小写字母、数字和特殊符号。</li>
</ul>
<p>填写完成后，勾选同意用户协议和隐私政策，点击「创建账号」即可提交注册。</p>

<h2>第三步：邮箱验证</h2>
<p>提交注册后，系统会向您填写的邮箱地址发送一封验证邮件。请登录邮箱，找到来自 17c.com 的验证邮件，点击邮件中的验证链接完成邮箱验证。如果未收到邮件，请检查垃圾邮件箱或点击「重新发送验证邮件」。</p>

<h2>第四步：完善个人资料</h2>
<p>验证邮箱后登录账号，系统会引导您进入个人资料设置页面。建议您完善以下信息以获得更好的平台体验：</p>
<ul>
<li>上传个人头像</li>
<li>填写个人简介</li>
<li>设置偏好语言和时区</li>
<li>绑定安全手机号（可选但建议设置）</li>
</ul>

<h2>第五步：开始探索 17c.com</h2>
<p>完成以上步骤后，您就可以畅享 17c.com 提供的全部功能了。建议您先浏览平台的功能导航页面，了解各个模块的用途和使用方式，快速找到最适合您的功能服务。</p>`,
    category: 'user-guide',
    author: '17c.com 官方团队',
    date: '2026-07-28',
    readTime: '5 分钟',
    imageSeq: 'article-guide-01',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['注册教程', '登录指南', '新手指南'],
  },
  {
    slug: 'platform-overview',
    title: '17c.com 平台全面解析：核心功能与服务一览',
    excerpt: '全面了解 17c.com 平台提供的各项核心功能模块，包括资源管理、数据分析、协作工具和个性化服务，掌握平台完整能力。',
    content: `<h2>17c.com 平台架构概览</h2>
<p>17c.com 是一个综合性在线服务平台，致力于为用户提供一站式数字化解决方案。平台采用模块化设计，各个功能模块既相互独立又有机协作，用户可以根据自身需求灵活组合使用。</p>

<h2>核心功能模块</h2>

<h3>1. 资源管理中心</h3>
<p>资源管理中心是 17c.com 的核心模块之一，提供文件上传、存储、分类管理和版本控制等功能。支持多种文件格式，包括文档、图片、视频和压缩包等。用户可以通过直观的文件夹结构管理资源，并设置不同的访问权限。</p>

<h3>2. 数据分析引擎</h3>
<p>17c.com 内置强大的数据分析工具，能够对平台数据进行多维度统计和可视化呈现。支持自定义报表生成、实时数据监控和历史趋势分析。无论是个人用户还是团队管理者，都可以通过数据分析模块洞察关键指标。</p>

<h3>3. 协作工作台</h3>
<p>协作工作台支持多人实时在线协作，包括文档协同编辑、任务分配与追踪、团队沟通等功能。用户可以创建项目空间，邀请团队成员加入，在一个统一的平台上完成从规划到执行的全流程管理。</p>

<h3>4. 个性化智能推荐</h3>
<p>基于用户的使用行为和偏好，17c.com 的智能推荐系统能够精准推送相关资源和内容。无论是学习资料、行业资讯还是工具推荐，系统都会不断学习优化，为您提供更加贴心的服务体验。</p>

<h3>5. 安全与隐私保护</h3>
<p>17c.com 采用多层安全防护机制，包括数据加密传输、访问控制、操作审计和备份容灾等。平台严格遵守数据保护法规，确保用户数据的安全性和隐私性。</p>

<h2>平台特色优势</h2>
<ul>
<li><strong>高效便捷：</strong>一站式服务模式，无需在多个平台间切换</li>
<li><strong>安全可靠：</strong>企业级安全标准，数据加密存储和传输</li>
<li><strong>持续更新：</strong>功能不断迭代优化，保持技术领先</li>
<li><strong>优质服务：</strong>7×24小时在线客服，快速响应用户需求</li>
</ul>`,
    category: 'platform-intro',
    author: '17c.com 官方团队',
    date: '2026-07-25',
    readTime: '8 分钟',
    imageSeq: 'article-overview-02',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['平台介绍', '功能概览', '核心服务'],
  },
  {
    slug: 'advanced-features-guide',
    title: '17c.com 高级功能使用指南：解锁平台全部潜力',
    excerpt: '深入探索 17c.com 的高级功能特性，包括自动化工作流、API 接口对接、高级搜索技巧和批量操作等进阶使用方法。',
    content: `<h2>高级功能概述</h2>
<p>当您已经熟悉 17c.com 的基础操作后，本文将带您探索平台提供的一系列高级功能，帮助您大幅提升工作效率，充分发挥平台的全部潜力。</p>

<h2>自动化工作流</h2>
<p>17c.com 的自动化工作流功能允许用户创建自定义的自动化规则，让重复性工作由系统自动完成。您可以设置触发条件、执行动作和通知方式，构建符合业务需求的自动化流程。常见应用场景包括：定时数据备份、文件自动分类、任务到期提醒等。</p>

<h2>API 接口对接</h2>
<p>对于有技术开发能力的用户，17c.com 提供了完整的 RESTful API 接口文档。通过 API，您可以将 17c.com 与现有的业务系统进行深度集成，实现数据的自动同步和功能的扩展。API 支持 OAuth 2.0 认证，确保接口调用的安全性。</p>

<h2>高级搜索技巧</h2>
<p>17c.com 的搜索引擎支持多种高级搜索语法：</p>
<ul>
<li><strong>精确匹配：</strong>使用双引号包裹关键词进行精确搜索</li>
<li><strong>布尔运算：</strong>使用 AND、OR、NOT 进行逻辑组合搜索</li>
<li><strong>字段限定：</strong>通过 type:、author:、date: 等前缀限定搜索范围</li>
<li><strong>文件类型过滤：</strong>按文档、图片、视频等类型筛选结果</li>
</ul>

<h2>批量操作</h2>
<p>17c.com 支持对多个资源进行批量操作，包括批量重命名、批量下载、批量权限设置和批量删除等。用户可以通过复选框选择多个项目，或使用全选功能快速选中当前视图内的所有项目，大幅提升操作效率。</p>

<h2>自定义仪表盘</h2>
<p>用户可以根据自己的使用习惯和关注重点，自定义个人仪表盘的布局和内容。您可以选择不同的数据卡片、图表组件和快捷入口，打造专属的工作面板。仪表盘配置支持保存多套方案，方便在不同工作场景间切换。</p>`,
    category: 'features',
    author: '17c.com 技术团队',
    date: '2026-07-20',
    readTime: '10 分钟',
    imageSeq: 'article-advanced-03',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['高级功能', 'API接口', '自动化', '进阶技巧'],
  },
  {
    slug: 'resource-download-guide',
    title: '17c.com 资源中心使用指南：发现与下载优质资源',
    excerpt: '详细讲解 17c.com 资源中心的使用方法，包括资源分类浏览、搜索筛选、下载管理和资源上传分享等完整功能操作指南。',
    content: `<h2>资源中心介绍</h2>
<p>17c.com 资源中心汇集了平台创作者和官方团队分享的海量优质资源，涵盖模板素材、设计资源、学习资料、工具插件等多个类别。无论您是设计师、开发者、学生还是职场人士，都能在这里找到对您有帮助的资源内容。</p>

<h2>浏览与发现资源</h2>
<p>资源中心提供了多种浏览方式：按分类浏览可以快速定位到感兴趣的资源类型；热门榜单展示了社区中最受欢迎的资源内容；编辑精选则是由平台编辑团队精心挑选的优质资源合集。此外，您还可以通过「猜你喜欢」功能获得个性化资源推荐。</p>

<h2>搜索与筛选</h2>
<p>在资源中心顶部的搜索栏中，您可以输入关键词搜索所需资源。搜索结果页面提供了丰富的筛选条件，包括文件格式、文件大小、上传时间、评分等级和下载量等。合理使用筛选功能可以帮您快速定位到最合适的资源。</p>

<h2>下载与管理</h2>
<p>找到想要的资源后，点击资源卡片进入详情页面即可查看完整介绍和预览。点击「下载」按钮，系统会将该资源保存到您的个人空间中。您可以在「我的下载」页面查看所有已下载资源的列表，并进行分类管理和本地导出操作。</p>

<h2>上传与分享</h2>
<p>17c.com 鼓励用户分享自己的优质资源。点击「上传资源」按钮，填写资源名称、描述、分类、标签等信息，上传资源文件后提交审核。审核通过后，您的资源将出现在资源中心中，供其他用户浏览和下载。分享优质资源还能获得平台积分奖励。</p>`,
    category: 'resources',
    author: '17c.com 运营团队',
    date: '2026-07-15',
    readTime: '7 分钟',
    imageSeq: 'article-resource-04',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['资源下载', '资源管理', '上传分享'],
  },
  {
    slug: 'common-questions-answers',
    title: '17c.com 常见问题汇总：您的疑惑这里都有答案',
    excerpt: '汇总了 17c.com 用户最常遇到的问题和官方解答，覆盖账号管理、功能使用、资源下载、安全隐私等多个方面。',
    content: `<h2>账号相关问题</h2>

<h3>Q: 忘记密码怎么办？</h3>
<p>在登录页面点击「忘记密码」，输入您注册时使用的邮箱地址，系统会发送密码重置链接到您的邮箱。点击链接后按照提示设置新密码即可。如果您同时忘记了注册邮箱，请联系客服协助处理。</p>

<h3>Q: 可以修改注册邮箱吗？</h3>
<p>可以。登录后在「账号设置」-「安全设置」中找到「修改邮箱」选项，按照提示进行新邮箱的验证和绑定。修改邮箱后，原邮箱将无法再用于登录。</p>

<h3>Q: 如何注销账号？</h3>
<p>在「账号设置」-「隐私与安全」页面底部找到「注销账号」选项。请注意，注销账号将永久删除您的所有数据且无法恢复，建议在注销前备份重要数据。</p>

<h2>功能使用问题</h2>

<h3>Q: 平台支持哪些文件格式？</h3>
<p>17c.com 支持上传和预览常见的文档格式（PDF、Word、Excel、PPT）、图片格式（JPG、PNG、GIF、WebP）、视频格式（MP4、WebM）和压缩包格式（ZIP、RAR、7Z）。单个文件大小限制为 2GB。</p>

<h3>Q: 如何邀请团队成员？</h3>
<p>在项目空间的「成员管理」页面，点击「邀请成员」，输入对方的邮箱地址并设置角色权限，发送邀请即可。对方接受邀请后即可加入您的项目空间。</p>

<h2>安全与隐私</h2>

<h3>Q: 平台数据安全吗？</h3>
<p>17c.com 采用银行级别的数据加密标准，所有数据传输均通过 HTTPS 加密。数据存储采用分布式架构并定期备份，确保数据安全和高可用性。平台已通过 ISO 27001 信息安全认证。</p>

<h3>Q: 如何设置资源的访问权限？</h3>
<p>在上传或编辑资源时，您可以设置资源的访问级别：公开（所有人可见）、仅团队成员、仅自己可见。对于敏感资源，还可以设置密码保护和访问有效期。</p>`,
    category: 'faq',
    author: '17c.com 支持团队',
    date: '2026-07-10',
    readTime: '6 分钟',
    imageSeq: 'article-faq-05',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['常见问题', 'FAQ', '问题解答'],
  },
  {
    slug: 'security-privacy-guide',
    title: '17c.com 安全与隐私保护详解：您的数据我们这样守护',
    excerpt: '深入了解 17c.com 平台的安全架构和隐私保护机制，包括数据加密、访问控制、合规认证和用户隐私权益保障措施。',
    content: `<h2>安全架构总览</h2>
<p>17c.com 将用户数据安全放在首位，构建了从基础设施到应用层的全链路安全防护体系。平台的安全架构遵循「纵深防御」原则，在每一层都设置了独立的安全机制，确保即使某一层防护被突破，仍有多重保障保护您的数据安全。</p>

<h2>数据传输安全</h2>
<p>所有在 17c.com 平台上的数据传输均采用 TLS 1.3 协议加密，这是目前业界最安全的传输层加密标准。无论是网页浏览、文件上传下载还是 API 接口调用，数据在网络中的传输都是完全加密的，有效防止中间人攻击和数据窃听。</p>

<h2>数据存储安全</h2>
<p>用户数据存储在采用 AES-256 加密的分布式存储系统中。敏感数据（如密码）采用 bcrypt 哈希算法加盐存储，即使是平台内部人员也无法查看明文密码。数据库定期自动备份并异地容灾，确保在极端情况下数据的可恢复性。</p>

<h2>访问控制与审计</h2>
<p>平台实施了严格的访问控制策略：基于角色的权限管理（RBAC）、多因素认证（MFA）、登录异常检测和操作审计日志。每次敏感操作都会被记录在审计日志中，用户可以随时查看自己账号的操作记录。</p>

<h2>隐私保护承诺</h2>
<p>17c.com 严格遵守《个人信息保护法》等数据保护法规，承诺：</p>
<ul>
<li>未经用户明确授权，不会向第三方提供用户个人信息</li>
<li>用户有权随时导出、修改或删除自己的个人数据</li>
<li>平台收集的数据仅用于提供和改进服务，不会用于其他目的</li>
<li>定期发布透明度报告，公开数据处理情况</li>
</ul>

<h2>安全合规认证</h2>
<p>17c.com 已通过以下安全认证：ISO 27001 信息安全管理体系认证、SOC 2 Type II 审计报告、等保三级认证。这些认证证明了平台在信息安全管理和数据保护方面达到了国际标准。</p>`,
    category: 'platform-intro',
    author: '17c.com 安全团队',
    date: '2026-07-05',
    readTime: '8 分钟',
    imageSeq: 'article-security-06',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['安全保护', '隐私政策', '数据加密'],
  },
  {
    slug: 'mobile-app-guide',
    title: '17c.com 移动端使用指南：随时随地高效办公',
    excerpt: '了解 17c.com 移动应用的功能特性，包括移动端特有的快捷操作、离线访问、推送通知和跨设备同步等实用功能介绍。',
    content: `<h2>移动端概述</h2>
<p>17c.com 提供了功能完善的移动端应用，支持 iOS 和 Android 两大主流平台。移动端保留了网页版的核心功能，并针对移动使用场景进行了优化设计，让您可以随时随地处理工作事务。</p>

<h2>安装与登录</h2>
<p>您可以通过 Apple App Store 或各大安卓应用市场搜索「17c.com」下载官方应用。安装完成后，使用您的 17c.com 账号直接登录即可。移动端支持面容 ID 和指纹识别快捷登录，兼顾安全性与便捷性。</p>

<h2>移动端特色功能</h2>
<ul>
<li><strong>离线访问：</strong>支持将重要文件设为离线可用，在没有网络的情况下也能查看</li>
<li><strong>推送通知：</strong>实时接收任务提醒、消息通知和系统公告</li>
<li><strong>快速扫码：</strong>支持二维码扫描快速登录网页版或分享资源</li>
<li><strong>手势操作：</strong>针对移动端优化的手势交互，操作更加直观高效</li>
<li><strong>跨设备同步：</strong>所有数据在手机、平板和电脑间实时同步</li>
</ul>

<h2>移动端使用技巧</h2>
<p>长按文件可以快速呼出操作菜单；向右滑动可以进行标记；双指缩放可调整预览视图。在设置中开启「省流量模式」可以减少移动数据消耗，开启「暗色模式」可以降低夜间使用的视觉疲劳。</p>`,
    category: 'user-guide',
    author: '17c.com 产品团队',
    date: '2026-06-28',
    readTime: '5 分钟',
    imageSeq: 'article-mobile-07',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['移动端', 'App使用', '跨设备'],
  },
  {
    slug: 'team-collaboration-best-practices',
    title: '17c.com 团队协作最佳实践：提升团队效率的秘诀',
    excerpt: '分享在 17c.com 平台上进行团队协作的最佳实践方法，包括项目管理、任务分配、沟通协作和绩效考核的实用经验。',
    content: `<h2>团队协作理念</h2>
<p>在 17c.com 平台上，团队协作不仅仅是功能的堆砌，而是一种高效的工作方式。通过合理的项目架构设计、清晰的角色分工和有效的沟通机制，团队可以将协作效率提升到一个全新的水平。</p>

<h2>项目空间设计</h2>
<p>建议按照以下结构组织项目空间：为每个独立项目创建专属空间，在空间内按功能模块划分子目录，使用标签系统对任务和文档进行分类标记。这样的结构既保证了信息的集中管理，又便于快速检索和权限控制。</p>

<h2>角色与权限管理</h2>
<p>合理设置团队成员的角色权限是高效协作的基础。建议采用以下角色划分：</p>
<ul>
<li><strong>管理员：</strong>负责项目空间的创建、删除和全局设置</li>
<li><strong>编辑者：</strong>负责内容的创建和修改</li>
<li><strong>评论者：</strong>可以查看和评论内容，但不可编辑</li>
<li><strong>观察者：</strong>只能查看内容，适合外部合作方</li>
</ul>

<h2>任务管理流程</h2>
<p>17c.com 的任务管理模块支持看板和列表两种视图。建议使用「待处理→进行中→审核中→已完成」的四阶段流程管理任务。为每个任务设置明确的负责人、截止日期和优先级，并通过 @提醒 功能及时通知相关人员。</p>

<h2>沟通与反馈</h2>
<p>利用 17c.com 内置的即时通讯和评论区功能进行团队沟通。建议在具体任务或文档下进行讨论，保持沟通的上下文关联性。定期使用「团队动态」功能分享工作进展和成果，增强团队凝聚力。</p>`,
    category: 'features',
    author: '17c.com 管理团队',
    date: '2026-06-20',
    readTime: '9 分钟',
    imageSeq: 'article-team-08',
    imageWidth: 1200,
    imageHeight: 675,
    tags: ['团队协作', '项目管理', '最佳实践'],
  },
];

export const faqs: FAQ[] = [
  {
    question: '17c.com 是什么类型的平台？',
    answer: '17c.com 是一个综合性在线服务平台，集资源管理、数据分析、团队协作和个性化服务等功能于一体，致力于为用户提供高效便捷的数字化工作解决方案。平台覆盖网页端和移动端，支持个人用户和团队用户使用。',
  },
  {
    question: '如何注册 17c.com 账号？',
    answer: '访问 www.17c.com 官网，点击右上角「注册」按钮，填写邮箱、用户名和密码，完成邮箱验证后即可成功注册。整个注册过程大约需要 2-3 分钟，完全免费。',
  },
  {
    question: '17c.com 是免费的吗？',
    answer: '17c.com 提供免费基础版和高级付费版两种方案。免费版包含核心功能和 5GB 存储空间，足以满足个人日常使用需求。高级版解锁更多存储空间、高级功能和优先技术支持，具体价格请查看官网定价页面。',
  },
  {
    question: '平台支持哪些设备和浏览器？',
    answer: '17c.com 网页版支持 Chrome、Firefox、Safari、Edge 等主流浏览器的最新两个版本。移动端支持 iOS 14+ 和 Android 8+ 的设备。建议保持浏览器和操作系统为最新版本以获得最佳体验。',
  },
  {
    question: '上传的文件有大小限制吗？',
    answer: '单个文件上传上限为 2GB（免费版）或 10GB（高级版）。对于超大文件，建议使用分卷压缩后分批上传。平台支持断点续传功能，大文件上传中断后可自动恢复。',
  },
  {
    question: '如何保障我的数据安全？',
    answer: '17c.com 采用 TLS 1.3 加密传输、AES-256 数据加密存储、定期自动备份和异地容灾等多重安全措施。平台已通过 ISO 27001 认证和等保三级认证，严格遵守数据保护法规，确保您的数据安全可靠。',
  },
  {
    question: '可以将 17c.com 与其他工具集成吗？',
    answer: '可以的。17c.com 提供了完善的 RESTful API 接口，支持与主流办公软件、项目管理工具和云存储服务的集成。具体集成方案请参考开发者文档或联系技术支持获取帮助。',
  },
  {
    question: '遇到问题如何联系客服？',
    answer: '您可以通过以下方式联系我们：在线客服（平台右下角聊天图标）、客服邮箱 support@17c.com、官方社交媒体账号。我们的客服团队提供 7×24 小时服务，通常在工作时间内 30 分钟内回复。',
  },
];

export const features = [
  {
    icon: 'ri-database-2-line',
    title: '资源管理',
    description: '高效的文件上传、存储、分类和版本控制，支持多种文件格式，让资源管理变得轻松有序。',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: '数据分析',
    description: '强大的数据统计和可视化工具，实时呈现关键指标，帮助您做出数据驱动的决策。',
  },
  {
    icon: 'ri-team-line',
    title: '团队协作',
    description: '支持多人实时协作、任务管理和即时沟通，让团队工作更加高效顺畅。',
  },
  {
    icon: 'ri-brain-line',
    title: '智能推荐',
    description: '基于 AI 的个性化推荐引擎，精准匹配您的需求，发现更多有价值的资源和内容。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '安全保障',
    description: '银行级数据加密和多重安全防护，通过国际安全认证，全方位守护您的数据安全。',
  },
  {
    icon: 'ri-smartphone-line',
    title: '多端同步',
    description: '网页、手机、平板全平台覆盖，数据实时同步，随时随地处理工作事务。',
  },
];

export const stats = [
  { value: '500万+', label: '注册用户' },
  { value: '2000万+', label: '资源数量' },
  { value: '99.9%', label: '服务可用率' },
  { value: '150+', label: '覆盖国家和地区' },
];

export const aboutInfo = {
  brandName: '17c.com',
  slogan: '连接智慧，赋能未来',
  description: '17c.com 是一家专注于数字化服务的创新平台，自成立以来始终秉承「用户至上、技术驱动」的理念，致力于为全球用户提供高效、安全、便捷的在线服务。我们汇聚了一流的研发团队和产品专家，通过持续不断的技术创新和产品迭代，打造了一个集资源管理、数据分析、协作工具和智能推荐于一体的综合性平台。',
  mission: '我们的使命是通过技术的力量，让每一个用户都能轻松获取和管理数字化资源，提升工作效率，释放创造力。我们相信，好的工具应该让复杂的事情变简单，让每个人都能专注于自己最擅长的事情。',
  vision: '成为全球领先的一站式数字化服务平台，连接亿万用户与海量优质资源，构建开放、共享、协作的数字生态。',
  values: [
    { title: '用户至上', description: '始终将用户需求放在首位，以用户满意度衡量产品价值' },
    { title: '技术驱动', description: '持续投入技术研发，以创新引领产品发展方向' },
    { title: '安全可靠', description: '将数据安全视为生命线，构建可信赖的平台服务' },
    { title: '开放共赢', description: '拥抱开放生态，与合作伙伴和用户共同成长' },
  ],
  milestones: [
    { year: '2018', event: '17c.com 平台正式上线，发布资源管理核心功能' },
    { year: '2019', event: '注册用户突破 100 万，推出团队协作功能模块' },
    { year: '2020', event: '完成 A 轮融资，上线数据分析引擎和智能推荐系统' },
    { year: '2021', event: '通过 ISO 27001 安全认证，移动端应用正式发布' },
    { year: '2022', event: '注册用户突破 500 万，资源总量超过 2000 万' },
    { year: '2023', event: '推出 API 开放平台，构建开发者生态体系' },
    { year: '2024', event: '通过等保三级认证，发布 AI 智能助手功能' },
    { year: '2025', event: '服务覆盖全球 150+ 国家和地区，持续引领行业创新' },
  ],
  contact: {
    email: 'support@17c.com',
    phone: '400-017-0017',
    address: '北京市海淀区中关村科技园区',
    workHours: '周一至周五 9:00 - 18:00（法定节假日除外）',
  },
};